import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  Inject,
  AfterViewInit,
} from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { Subject } from 'rxjs/Subject';
import { finalize, takeUntil } from 'rxjs/operators';
import { NbToastrService } from '@nebular/theme';
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import {
  getDeepFromObject,
  NbAuthOAuth2JWTToken,
  NbAuthResult,
  NbAuthService,
  NbTokenService,
  NB_AUTH_OPTIONS,
} from '@nebular/auth';

import { User, UserData } from '../../../@core/interfaces/common/users';
import { UserStore } from '../../../@core/stores/user.store';
import {
  SubscriptionPlanData,
  SubscriptionPlan,
} from '../../../@core/interfaces/common/CalorieFriend/subscriptionPlan';
import {
  Country,
  CountryData,
} from '../../../@core/interfaces/common/countries';
import { ConversionFactorsData } from 'app/@core/utils/conversionFactors.data';
import { AppSettingsService } from 'app/@core/backend/common/services/appSettings.service';
import { NbRoleProvider } from '@nebular/security';
import { ROLES } from 'app/@auth/roles';
import { AppSettings } from 'app/@core/interfaces/common/appSettings';
import { SettingsService } from 'app/@core/backend/common/services/settings.service';
import { Settings } from 'app/@core/interfaces/common/settings';
import { JSONStringify } from './../../../SupportOps';
import { AppSettingsConfigurations } from 'app/@components/app-settings/app-settings.component';
import { is } from 'date-fns/locale';

export enum UserFormMode {
  VIEW = 'View',
  EDIT = 'Edit',
  ADD = 'Add',
  EDIT_SELF = 'EditSelf',
}

@Component({
  selector: 'ngx-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
})
export class UserComponent implements OnInit, AfterViewInit, OnDestroy {
  UserFormMode = UserFormMode;
  userForm: FormGroup;
  ConversionFactorsPreference = ConversionFactorsData;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.Canada,
  ];

  ContactAndBillingInfoLabel = 'Contact and Billing Info';
  ContactInfoLabel = 'Contact Info';
  BillingInfoLabel = 'Billing Info';
  PhoneInfoLabel = 'Phone Number';
  SubscriptionPlanLabel = 'Subscription Plan';
  appSettings: AppSettings;
  deviceToken="cHT0GeujRhy-q5Bs7SVL-3:APA91bFTYMLwYfWSiZwMCbG4dfTn6uTikcvIaJqtEv_YUSKfP2P3Dfg0SpwEKBfEYDxTWEDvMnxHemkLaQcrNEcfJENXugNx_ACtqzUmfGVx3kRLt82AJiDzyDoNZeUM82o36G8uV6Si";

  ChangePasswordLabel: string = 'Change Password';
  CurrentPasswordLabel: string = 'Current Password';
  NewPasswordLabel: string = 'New Password';
  ConfirmPasswordLabel: string = 'Confirm Password';
  PasswordsValidationMessage: string =
    'New password and confirm password does not match!';

  strategy: string = this.getConfigValue('forms.resetPassword.strategy');
  minLength: number = this.getConfigValue(
    'forms.validation.password.minLength'
  );
  maxLength: number = this.getConfigValue(
    'forms.validation.password.maxLength'
  );
  isAdmin: boolean = false;
  resetPasswordForm: FormGroup;
  configurations: AppSettingsConfigurations = {
    appStorePassword: null,
    keyId: null,
    issuerId: null,
    username: null,
    teamId: null,
    teamName: null,
    bundleId: null,
    branchName: null,
    token: null,
  };
  get currentPassword() {
    return this.resetPasswordForm.get('currentPassword');
  }
  get Password() {
    return this.resetPasswordForm.get('Password');
  }
  get confirmPassword() {
    return this.resetPasswordForm.get('confirmPassword');
  }

  protected readonly unsubscribe$ = new Subject<void>();

  get firstName() {
    return this.userForm.get('firstName');
  }
  get lastName() {
    return this.userForm.get('lastName');
  }
  // get login() { return this.userForm.get('login'); }
  // get email() { return this.userForm.get('email'); }
  get phoneNumber() {
    return this.userForm.get('phoneNumber');
  }
  get businessName() {
    return this.userForm.get('businessName');
  }
  get secondAddress() {
    return this.userForm.get('secondAddress');
  }
  get street() {
    return this.userForm.get('address').get('street');
  }
  get city() {
    return this.userForm.get('address').get('city');
  }
  get country() {
    return this.userForm.get('address').get('country');
  }
  get zipCode() {
    return this.userForm.get('address').get('zipCode');
  }

  get conversionFactorsValue() {
    return this.userForm.get('conversionFactorsPreference');
  }

  mode: UserFormMode;
  setViewMode(viewMode: UserFormMode) {
    this.mode = viewMode;
  }

  currentUser: User;
  userSettings: Settings;
  selectedPlanCode: string = '';
  selectedPlan: SubscriptionPlan;
  appStoreUrl: string;
  playStoreUrl: string;
  public isLoading: boolean = false;

  observablePlans: Observable<SubscriptionPlan[]>;

  Countries: Observable<Country[]>;

  constructor(
    private usersService: UserData,
    @Inject(NB_AUTH_OPTIONS) protected options = {},
    private route: ActivatedRoute,
    private authService: NbAuthService,
    private settingService: SettingsService,
    private tokenService: NbTokenService,
    private countriesService: CountryData,
    private userStore: UserStore,
    private toasterService: NbToastrService,
    protected subscriptionService: SubscriptionPlanData,
    protected cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private appSettingService: AppSettingsService,
    private roleProvider: NbRoleProvider,
  ) {}

  ngOnInit(): void {
    this.roleProvider.getRole().subscribe(role => {
      const roles = role instanceof Array ? role : [role];
      this.isAdmin = roles.some((x) => x && x.toLowerCase() === ROLES.ADMIN);
      if(this.isAdmin) {
        const id = this.route.snapshot.paramMap.get('id');
        this.settingService.getUserSettings(Number.parseInt(id)).subscribe(res => {
          if(res)
          this.userSettings = res;
        else {
          this.userSettings = {
            id: Number.parseInt(id),
            appLogo: null,
            logo: null,
            color: null,
            appColor: null,
            themeName: null,
           appStoreUrl: null,
           playStoreUrl: null,
          }
        }
        });
      }
    });
    this.initUserForm();
    this.loadUserData();
    this.initChangePasswordForm();
    this.observablePlans = this.subscriptionService.getAvailableList();

    // load countries
    this.Countries = this.countriesService.list();
  }

  initChangePasswordForm() {
    const passwordValidators = [
      Validators.required,
      Validators.minLength(this.minLength),
      Validators.maxLength(this.maxLength),
    ];

    this.resetPasswordForm = this.fb.group({
      currentPassword: this.fb.control('', [...passwordValidators]),
      Password: this.fb.control('', [...passwordValidators]),
      confirmPassword: this.fb.control('', [...passwordValidators]),
    });
  }

  initUserForm() {
    this.userForm = this.fb.group({
      id: this.fb.control(''),
      role: this.fb.control(''),
      email: this.fb.control('', [
        Validators.required,
        Validators.email
      ]),
      firstName: this.fb.control('', [
        Validators.minLength(3),
        Validators.maxLength(40),
      ]),
      lastName: this.fb.control('', [
        Validators.minLength(3),
        Validators.maxLength(40),
      ]),
      conversionFactorsPreference: this.fb.control('g'),
      // login: this.fb.control('', [Validators.required, Validators.minLength(6), Validators.maxLength(20)]),
      // email: this.fb.control('', [
      //   Validators.required,
      //   Validators.pattern(EMAIL_PATTERN),
      // ]),
      phoneNumber: this.fb.control('', [Validators.required]),
      businessName: this.fb.control('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      isCustomAppInProduction: this.fb.control(null),
      customAppEnabled: this.fb.control(null),
      address: this.fb.group({
        street: this.fb.control('', [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(1024),
        ]),
        secondAddress: this.fb.control(''),
        city: this.fb.control('', [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(1024),
        ]),
        country: this.fb.control('', [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(1024),
        ]),
        zipCode: this.fb.control(''),
      }),
    });
  }

  get canEdit(): boolean {
    return this.mode !== UserFormMode.VIEW;
  }

  loadUserData() {
    const id = this.route.snapshot.paramMap.get('id');
    const isProfile = this.route.snapshot.queryParamMap.get('profile');
    if (isProfile) {
      this.setViewMode(UserFormMode.EDIT_SELF);
      this.loadUser();
    } else {
      if (id) {
        const currentUserId = this.userStore.getUser()?.id;
        this.setViewMode(
          currentUserId?.toString() === id
            ? UserFormMode.EDIT_SELF
            : UserFormMode.EDIT
        );
        this.loadUser(id);
      } else {
        this.setViewMode(UserFormMode.ADD);
      }
    }
  }

  tokenFileUpload(event: any): void{
    const file = event.target.files[0];
    if (!file) {
        return;
    }

    const reader = new FileReader();
    reader.onload = (fileLoadEvent) => {
        const text = fileLoadEvent.target.result;
            this.configurations.token = text as string;
    }
    reader.readAsText(file);
  }

  loadUser(id?) {
    this.isLoading = true;
    const loadUser =
      this.mode === UserFormMode.EDIT_SELF
        ? this.usersService.getCurrentUser()
        : this.usersService.get(id);

    loadUser.pipe(takeUntil(this.unsubscribe$),
    finalize(() => this.isLoading = false)).subscribe((user: User) => {
      this.currentUser = user;
      this.selectedPlanCode = user.planCode;

      this.subscriptionService.getByCode(user.planCode).subscribe((plan) => {
        this.selectedPlan = plan;
      });
      this.userForm.setValue({
        id: user.id ? user.id : '',
        role: user.role ? user.role : '',
        firstName: user.firstName ? user.firstName : '',
        lastName: user.lastName ? user.lastName : '',
        phoneNumber: user.phoneNumber ? user.phoneNumber : '',
        businessName: user.businessName ? user.businessName : '',
        email: user.email ? user.email : '',
        conversionFactorsPreference: user.conversionFactorsPreference? user.conversionFactorsPreference: 'g',
        address: {
          street:
            user.address && user.address.street ? user.address.street : '',
          secondAddress:
            user.address && user.address.secondAddress
              ? user.address.secondAddress
              : '',
          city: user.address && user.address.city ? user.address.city : '',
          country:
            user.address && user.address.country ? user.address.country : '',
          zipCode:
            user.address && user.address.zipCode ? user.address.zipCode : '',
        },
        isCustomAppInProduction: user.isCustomAppInProduction,
        customAppEnabled: user.customAppEnabled,
      });

      // this is a place for value changes handling
      // this.userForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {   });
    });
  }

  convertToUser(value: any): User {
    const user: User = {
      ...value,
      phoneNumber: value.phoneNumber.internationalNumber,
    };
    return user;
  }

  changeIsCustomAppInProduction(event) {
    this.userForm.patchValue({
      isCustomAppInProduction: event,
    });
  }

  changeCustomAppEnabled(event) {
    this.userForm.patchValue({
      customAppEnabled: event,
    });
  }

  get isCustomAppEnabled(): boolean {
    return this.userForm.get('customAppEnabled').value;
  }

  get isCustomAppInProduction(): boolean {
    return this.userForm.get('isCustomAppInProduction').value;
  }

  save() {
    const user: User = this.convertToUser(this.userForm.value);
    // user.email = this.currentUser.email;
    // user.login = this.currentUser.login;

    let observable = new Observable<User>();
    if (this.mode === UserFormMode.EDIT_SELF) {
      this.usersService.updateCurrent(user).subscribe(
        (result: any) => {
          this.tokenService.set(
            new NbAuthOAuth2JWTToken(result, 'email', new Date())
          );
          this.handleSuccessResponse();
          this.userStore.setUser(user);
        },
        () => {
          this.handleWrongResponse();
        }
      );
    } else {
      observable = user.id
        ? this.usersService.update(user)
        : this.usersService.create(user);
      //  this.userSettings.color = JSON.stringify(this.userSettings.color);
      //   this.settingService.updateUserSettings({...this.userSettings, appLogo: null, logo: null, color: JSON.stringify(this.userSettings.color), appColor: JSON.stringify(this.userSettings.appColor)}).subscribe(res => {
      //     console.log(res);
      //   });
        // this.appSettingService.update({...this.appSettings, configuration: JSONStringify(this.configurations)}).subscribe(res => {
        //   console.log(res);
        // });
    }
    this.isLoading = true;

    observable.pipe(takeUntil(this.unsubscribe$),
    finalize(() => this.isLoading = false)).subscribe(
      () => {
        this.handleSuccessResponse();
      },
      () => {
        this.handleWrongResponse();
      }
    );
    // if(this.isAdmin) {
    //   this.appSettingService.update(this.appSettings).subscribe(res => {
    //     console.log(res);
    //   })
    // }
  }

  saveAppSettings({user, settings}): void {
    const userData: User = this.convertToUser({...this.userForm.value,
      isCustomAppInProduction: user.isCustomAppInProduction,
       customAppEnabled: user.customAppEnabled});
       console.log(userData);
    this.usersService.update(
      userData
    ).subscribe(
      (result: User) => {
        this.handleSuccessResponse();
        this.currentUser = user;
        this.userStore.setUser(user);
      },
      () => {
        this.handleWrongResponse();
      }
    );
  }

  changePassword() {
    const passwordData: any = this.resetPasswordForm.value;

    this.authService
      .resetPassword(this.strategy, passwordData)
      .subscribe((result: NbAuthResult) => {
        if (result.isSuccess()) {
          this.handleResetPasswordSuccessResponse();
          // this.messages = result.getMessages();
        } else {
          this.handleResetPasswordWrongResponse();
          // this.errors = result.getErrors();
        }
        this.resetPasswordForm.reset();
      });
  }

  savePlan() {
    if (this.mode === UserFormMode.EDIT_SELF) {
      if (this.selectedPlanCode !== this.currentUser.planCode) {
        if (
          !confirm('Are you sure you want to change the subscription plan?')
        ) {
          this.selectedPlanCode = this.currentUser.planCode;
          return;
        }

        this.usersService
          .updateCurrentUserPlan(this.currentUser, this.selectedPlanCode)
          .subscribe(
            () => {
              this.currentUser.planCode = this.selectedPlanCode;
              this.handlePlanSuccessResponse();
              this.loadUser();
            },
            () => {
              this.handlePlanWrongResponse();
            }
          );
      }
    }
  }

  runBuild() {
    const id = this.route.snapshot.paramMap.get('id');
    this.appSettingService.runBuild(parseInt(id)).subscribe(res => {
      console.log(res);
    });
  }

  handleSuccessResponse() {
    this.toasterService.success(
      '',
      `Item ${this.mode === UserFormMode.ADD ? 'created' : 'updated'}!`
    );
    this.back();
  }

  handleWrongResponse() {
    this.toasterService.danger('', `This email has already taken!`);
  }

  handleResetPasswordSuccessResponse() {
    this.toasterService.success('', `The password has been changed!`);
  }

  handleResetPasswordWrongResponse() {
    this.toasterService.danger('', `Changing password failed!`);
  }

  handlePlanSuccessResponse() {
    this.toasterService.success('', `The subscription plan has been changed!`);
    this.back();
  }

  handlePlanWrongResponse() {
    this.toasterService.danger('', `The subscription plan cannot be changed!`);
  }

  back() {
    // this.location.back();
    // this.router.navigate(['/admin/users/list']);
  }

  subscription: Subscription;

  fragment: string;

  public ngAfterViewInit(): void {
    this.subscription = this.route.fragment.subscribe((fragment) => {
      try {
        this.fragment = fragment;
        setTimeout(() => this.scrollToAnchor(), 10);
      } catch (e) {}
    });
  }

  scrollToAnchor(): void {
    try {
      if (this.fragment) {
        document.querySelector('#' + this.fragment).scrollIntoView();
      }
    } catch (e) {}
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getConfigValue(key: string): any {
    return getDeepFromObject(this.options, key, null);
  }

  sendNotifications(){
    this.appSettingService.sendNotifications(this.deviceToken).subscribe(() => {
      console.log("Sended....");
    })
  }
}
